import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Loader2, Plus, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";

const eur = (n) =>
  `${(Number(n) || 0).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

const emptyLine = () => ({
  description: "",
  units: 1,
  unit_price: 0,
  vat_rate: 0,
  irpf_rate: 0,
});

/**
 * Diálogo "Factura" del apartado Documentación. Pide al backend la propuesta
 * de factura del curso (concepto por tipo + CCAA, tarifa del catálogo,
 * desplazamiento por km, fechas, horario y nº de alumnos), deja revisarla y
 * crea la factura en Facturación.
 */
export const CourseInvoiceDialog = ({ courseId, open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: "",
    date: "",
    due_date: "",
    number: "",
    lines: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    setProposal(null);
    try {
      const [pRes, cRes] = await Promise.all([
        adminApi.get(`/admin/invoicing/from-course/${courseId}`),
        adminApi.get("/admin/invoicing/clients"),
      ]);
      const p = pRes.data || {};
      setProposal(p);
      setClients(cRes.data || []);
      setForm({
        client_id: p.issefe_client?.id || p.client?.id || "",
        date: p.date || "",
        due_date: p.due_date || "",
        number: p.suggested_number || "",
        lines: (p.lines || []).map((l) => ({ ...l })),
      });
    } catch (e) {
      logError("No se pudo preparar la factura del curso", e);
      toast.error(
        e?.response?.data?.detail || "No se pudo preparar la factura del curso."
      );
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [courseId, onOpenChange]);

  useEffect(() => {
    if (open && courseId) load();
    // load es estable (useCallback) y depende de courseId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courseId]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    for (const l of form.lines) {
      const base = (parseInt(l.units, 10) || 0) * (Number(l.unit_price) || 0);
      subtotal += base;
      vat += (base * (Number(l.vat_rate) || 0)) / 100;
    }
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      total: Math.round((subtotal + vat) * 100) / 100,
    };
  }, [form.lines]);

  const selectedClient = clients.find((c) => c.id === form.client_id);
  const alreadyInvoiced = proposal?.already_invoiced;

  const setLine = (idx, key, value) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, i) => (i === idx ? { ...l, [key]: value } : l)),
    }));

  const handleCreate = async () => {
    if (!form.client_id) {
      toast.error("Elige el cliente al que se factura.");
      return;
    }
    if (!/^\d{4}[-/]\d{1,6}$/.test(form.number.trim())) {
      toast.error("Indica el número de factura con el formato 2026-0113.");
      return;
    }
    const lines = form.lines.filter((l) => (l.description || "").trim());
    if (!lines.length) {
      toast.error("La factura necesita al menos un concepto.");
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.post("/admin/invoicing/invoices", {
        client_id: form.client_id,
        course_id: courseId,
        date: form.date,
        due_date: form.due_date,
        requested_number: form.number.trim(),
        lines: lines.map((l) => ({
          description: l.description.trim(),
          units: Math.max(1, parseInt(l.units, 10) || 1),
          unit_price: Number(l.unit_price) || 0,
          vat_rate: Number(l.vat_rate) || 0,
          irpf_rate: Number(l.irpf_rate) || 0,
        })),
        footer: proposal?.footer || null,
      });
      toast.success(
        `Factura ${res.data?.number || ""} creada (${eur(res.data?.total)}).`
      );
      onOpenChange(false);
    } catch (e) {
      logError("No se pudo crear la factura", e);
      toast.error(
        e?.response?.data?.detail || "No se pudo crear la factura."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="course-invoice-dialog"
      >
        <DialogHeader>
          <DialogTitle className="heading-font text-2xl font-extrabold">
            Factura del curso
          </DialogTitle>
          <DialogDescription>
            Concepto, tarifa, desplazamiento, fechas y nº de alumnos se rellenan
            automáticamente. Al confirmar se crea la <strong>factura</strong> en
            Facturación, lista para descargar o editar.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-zinc-500">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-3" />
            Preparando la factura…
          </div>
        ) : !proposal ? null : (
          <div className="space-y-5">
            {alreadyInvoiced && (
              <div
                className="rounded-md border-2 border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900"
                data-testid="invoice-already-warning"
              >
                Este curso ya tiene factura ({alreadyInvoiced.number}).
                Elimínala antes de volver a facturarlo.
              </div>
            )}

            {(proposal.warnings || []).map((w, i) => (
              <div
                key={i}
                className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                data-testid={`invoice-warning-${i}`}
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{w}</span>
              </div>
            ))}

            {/* Resumen del curso */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-md bg-zinc-50 border border-zinc-200 p-4 text-sm">
              {[
                ["Empresa del curso", proposal.course?.client_name || "—"],
                ["CCAA", proposal.course?.region_name || "—"],
                [
                  "Tipo",
                  { reciclaje: "Reciclaje", ppaa: "Primeros Auxilios" }[proposal.course?.course_type] || "Inicial",
                ],
                ["Fecha(s)", proposal.footer?.training_date || "—"],
                ["Horario", proposal.footer?.training_schedule || "—"],
                ["Horas", proposal.footer?.training_hours || "—"],
                ["Alumnos", String(proposal.students ?? "—")],
                [
                  "Km (ida)",
                  proposal.km_one_way != null
                    ? `${Math.round(proposal.km_one_way)} km`
                    : "—",
                ],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                    {label}
                  </div>
                  <div className="font-semibold text-zinc-900 truncate" title={value}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {/* Destinatario */}
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1.5">
                Se factura a
              </label>
              <Select
                value={form.client_id}
                onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}
              >
                <SelectTrigger
                  className="h-11 border border-zinc-300"
                  data-testid="invoice-client-select"
                >
                  <SelectValue placeholder="Elige el cliente" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                      {c.nif ? ` · ${c.nif}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                {proposal.issefe_client && (
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({ ...f, client_id: proposal.issefe_client.id }))
                    }
                    className="px-2.5 py-1 rounded border border-zinc-300 hover:bg-zinc-100 font-medium"
                    data-testid="invoice-target-issefe"
                  >
                    Facturar a ISSEFE
                  </button>
                )}
                {proposal.course_client && (
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({ ...f, client_id: proposal.course_client.id }))
                    }
                    className="px-2.5 py-1 rounded border border-zinc-300 hover:bg-zinc-100 font-medium"
                    data-testid="invoice-target-course-client"
                  >
                    Facturar a {proposal.course_client.name}
                  </button>
                )}
                {selectedClient && !selectedClient.nif && (
                  <span className="text-amber-700">
                    Ojo: este cliente no tiene NIF guardado.
                  </span>
                )}
              </div>
            </div>

            {/* Fecha + número */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1.5">
                  Fecha de factura
                </label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, date: e.target.value }))
                  }
                  className="h-11 border border-zinc-300"
                  data-testid="invoice-date-input"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1.5">
                  Vencimiento
                </label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, due_date: e.target.value }))
                  }
                  className="h-11 border border-zinc-300"
                  data-testid="invoice-due-date-input"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1.5">
                  Nº de factura *
                </label>
                <Input
                  value={form.number}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, number: e.target.value }))
                  }
                  placeholder={proposal.suggested_number || "2026-0113"}
                  className="h-11 border border-zinc-300 font-mono"
                  data-testid="invoice-number-input"
                />
                <p className="mt-1 text-[11px] text-zinc-500">
                  {`Último usado: ${proposal.suggested_number
                    ? Number(proposal.suggested_number.split("-")[1]) - 1
                    : 0}. Se propone ${proposal.suggested_number}.`}
                </p>
              </div>
            </div>

            {/* Conceptos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
                  Conceptos
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }))
                  }
                  className="inline-flex items-center gap-1 text-xs font-medium text-zinc-700 hover:text-zinc-950"
                  data-testid="invoice-add-line"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Añadir línea
                </button>
              </div>
              <div className="space-y-2">
                {form.lines.map((l, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-center"
                    data-testid={`invoice-line-${idx}`}
                  >
                    <Input
                      value={l.description}
                      onChange={(e) => setLine(idx, "description", e.target.value)}
                      placeholder="Concepto…"
                      className="col-span-6 h-10 border border-zinc-300 text-sm"
                      data-testid={`invoice-line-desc-${idx}`}
                    />
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={l.units}
                      onChange={(e) =>
                        setLine(idx, "units", e.target.value.replace(/[^0-9]/g, ""))
                      }
                      onBlur={(e) =>
                        setLine(
                          idx,
                          "units",
                          String(Math.max(1, parseInt(e.target.value, 10) || 1))
                        )
                      }
                      className="col-span-1 h-10 border border-zinc-300 text-sm text-center"
                      data-testid={`invoice-line-units-${idx}`}
                      title="Unidades"
                    />
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={l.unit_price}
                      onChange={(e) => setLine(idx, "unit_price", e.target.value)}
                      className="col-span-2 h-10 border border-zinc-300 text-sm text-right"
                      data-testid={`invoice-line-price-${idx}`}
                      title="Precio unitario (€)"
                    />
                    <div className="col-span-2 text-right text-sm font-semibold whitespace-nowrap">
                      {eur((parseInt(l.units, 10) || 0) * (Number(l.unit_price) || 0))}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          lines: f.lines.filter((_, i) => i !== idx),
                        }))
                      }
                      className="col-span-1 justify-self-center text-zinc-400 hover:text-red-600"
                      data-testid={`invoice-line-remove-${idx}`}
                      aria-label="Quitar línea"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-end gap-6 text-sm">
                <div className="text-zinc-600">
                  Base: <strong>{eur(totals.subtotal)}</strong>
                </div>
                <div className="text-zinc-600">
                  IVA: <strong>{eur(totals.vat)}</strong>
                </div>
                <div
                  className="text-zinc-950 text-base font-extrabold"
                  data-testid="invoice-total"
                >
                  Total: {eur(totals.total)}
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-11 border border-zinc-300"
            data-testid="invoice-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            disabled={loading || saving || !!alreadyInvoiced}
            className="h-11 bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="invoice-create-button"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Receipt className="h-4 w-4 mr-2" />
            )}
            Crear factura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
